import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './hooks/useAuth';
import { LMSAuthProvider, LMSProtectedRoute } from './hooks/useLMSAuth';

// Company pages
import Register from './pages/Register';
import Login from './pages/Login';
import Dashboard from './pages/Dashboard';
import PostJob from './pages/PostJob';
import Applications from './pages/Applications';
import CompanyProfile from './pages/CompanyProfile';
import Billing from './pages/Billing';
import Analytics from './pages/Analytics';
import Support from './pages/company/Support';

// Admin pages
import AdminLogin from './pages/admin/AdminLogin';
import AdminDashboard from './pages/admin/AdminDashboard';
import AdminCompanies from './pages/admin/AdminCompanies';
import AdminJobs from './pages/admin/AdminJobs';
import AdminStats from './pages/admin/AdminStats';
import AdminUsers from './pages/admin/AdminUsers';
import AdminApplications from './pages/admin/AdminApplications';
import AdminManagers from './pages/admin/AdminManagers';
import AdminTutors from './pages/admin/AdminTutors';
import AdminLMS from './pages/admin/AdminLMS';

// Manager pages
import ManagerLogin from './pages/manager/ManagerLogin';
import ManagerDashboard from './pages/manager/ManagerDashboard';
import ManagerCompanies from './pages/manager/ManagerCompanies';
import ManagerTickets from './pages/manager/ManagerTickets';

import LMSRegister      from './pages/lms/LMSRegister';
import LMSLogin         from './pages/lms/LMSLogin';
import LMSDashboard     from './pages/lms/LMSDashboard';
import LMSStudents      from './pages/lms/LMSStudents';
import LMSClasses       from './pages/lms/LMSClasses';
import LMSTimetable     from './pages/lms/LMSTimetable';
import LMSFeed          from './pages/lms/LMSFeed';
import LMSEvents        from './pages/lms/LMSEvents';
import LMSReports       from './pages/lms/LMSReports';
import LMSSettings      from './pages/lms/LMSSettings';
import LMSTeachers      from './pages/lms/LMSTeachers';
import LMSAssignments   from './pages/lms/LMSAssignments';
import LMSAttendance    from './pages/lms/LMSAttendance';
import LMSCourses       from './pages/lms/LMSCourses';
import LMSExams         from './pages/lms/LMSExams';
import LMSCalendar      from './pages/lms/LMSCalendar';
import LMSLibrary       from './pages/lms/LMSLibrary';
import LMSFees          from './pages/lms/LMSFees';
import LMSNotifications from './pages/lms/LMSNotifications';
import LMSAccessControl from './pages/lms/LMSAccessControl';

import LMSStaffLogin from './pages/lms/LMSStaffLogin';


// Detect subdomains
const isAdminDomain = window.location.hostname.includes('admin.');
const isManagerDomain = window.location.hostname.includes('manager.');

// ── Loader ────────────────────────────────────────────
function Loader() {
  return (
    <div style={{
      minHeight: '100vh',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      background: '#F8FAFF',
      gap: 16,
    }}>
      <div style={{
        width: 44, height: 44,
        background: 'linear-gradient(135deg,#4F8EF7,#7C3AED)',
        borderRadius: 12,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        color: '#fff',
        fontWeight: 900,
        fontSize: 20,
      }}>U</div>
      <div style={{
        width: 32, height: 32, borderRadius: '50%',
        border: '3px solid #E2E8F0',
        borderTopColor: '#4F8EF7',
        animation: 'spin 0.8s linear infinite',
      }} />
    </div>
  );
}

// ── Route Guards ──────────────────────────────────────
function CompanyRoute({ children }) {
  const { user, company, isAdmin, isManager, loading } = useAuth();
  if (loading) return <Loader />;
  if (!user) return <Navigate to="/login" replace />;
  if (isAdmin) return <Navigate to="/admin/dashboard" replace />;
  if (isManager) return <Navigate to="/manager/dashboard" replace />;
  if (!company) return <Navigate to="/login" replace />;
  return children;
}

function AdminRoute({ children }) {
  const { user, isAdmin, loading } = useAuth();
  if (loading) return <Loader />;
  if (!user) return <Navigate to="/admin/login" replace />;
  if (!isAdmin) return <Navigate to="/login" replace />;
  return children;
}

function ManagerRoute({ children }) {
  const { user, isManager, loading } = useAuth();
  if (loading) return <Loader />;
  if (!user) return <Navigate to="/manager/login" replace />;
  if (!isManager) return <Navigate to="/manager/login" replace />;
  return children;
}

function PublicRoute({ children }) {
  const { user, isAdmin, isManager, company, loading } = useAuth();
  if (loading) return <Loader />;
  if (user && isAdmin) return <Navigate to="/admin/dashboard" replace />;
  if (user && isManager) return <Navigate to="/manager/dashboard" replace />;
  if (user && company) return <Navigate to="/dashboard" replace />;
  return children;
}

function AdminPublicRoute({ children }) {
  const { user, isAdmin, loading } = useAuth();
  if (loading) return <Loader />;
  if (user && isAdmin) return <Navigate to="/admin/dashboard" replace />;
  return children;
}

function ManagerPublicRoute({ children }) {
  const { user, isManager, loading } = useAuth();
  if (loading) return <Loader />;
  if (user && isManager) return <Navigate to="/manager/dashboard" replace />;
  return children;
}

// ── App ───────────────────────────────────────────────
export default function App() {
  return (
    <AuthProvider>
      <LMSAuthProvider>
      <BrowserRouter>
        <Routes>

          {/* ── Root redirect ── */}
          <Route path="/" element={
            <Navigate to={
              isAdminDomain ? '/admin/login'
              : isManagerDomain ? '/manager/login'
              : '/login'
            } replace />
          } />

          {/* ── Company Public Routes ── */}
          <Route path="/register" element={
            <PublicRoute><Register /></PublicRoute>
          } />
          <Route path="/login" element={
            <PublicRoute><Login /></PublicRoute>
          } />

          {/* ── Company Protected Routes ── */}
          <Route path="/dashboard" element={
            <CompanyRoute><Dashboard /></CompanyRoute>
          } />
          <Route path="/post-job" element={
            <CompanyRoute><PostJob /></CompanyRoute>
          } />
          <Route path="/post-job/:id" element={
            <CompanyRoute><PostJob /></CompanyRoute>
          } />
          <Route path="/applications" element={
            <CompanyRoute><Applications /></CompanyRoute>
          } />
          <Route path="/profile" element={
            <CompanyRoute><CompanyProfile /></CompanyRoute>
          } />
          <Route path="/billing" element={
            <CompanyRoute><Billing /></CompanyRoute>
          } />
          <Route path="/analytics" element={
            <CompanyRoute><Analytics /></CompanyRoute>
          } />
          <Route path="/support" element={
            <CompanyRoute><Support /></CompanyRoute>
          } />

          {/* ── Admin Public Routes ── */}
          <Route path="/admin" element={
            <Navigate to="/admin/login" replace />
          } />
          <Route path="/admin/login" element={
            <AdminPublicRoute><AdminLogin /></AdminPublicRoute>
          } />

          {/* ── Admin Protected Routes ── */}
          <Route path="/admin/dashboard" element={
            <AdminRoute><AdminDashboard /></AdminRoute>
          } />
          <Route path="/admin/companies" element={
            <AdminRoute><AdminCompanies /></AdminRoute>
          } />
          <Route path="/admin/jobs" element={
            <AdminRoute><AdminJobs /></AdminRoute>
          } />
          <Route path="/admin/stats" element={
            <AdminRoute><AdminStats /></AdminRoute>
          } />
          <Route path="/admin/users" element={
            <AdminRoute><AdminUsers /></AdminRoute>
          } />
          <Route path="/admin/applications" element={
            <AdminRoute><AdminApplications /></AdminRoute>
          } />
          <Route path="/admin/managers" element={
            <AdminRoute><AdminManagers /></AdminRoute>
          } />
          <Route path="/admin/tutors" element={
            <AdminRoute><AdminTutors /></AdminRoute>
          } />
          <Route path="/admin/lms" element={
            <AdminRoute><AdminLMS /></AdminRoute>
          } />

          {/* ── Manager Public Routes ── */}
          <Route path="/manager" element={
            <Navigate to="/manager/login" replace />
          } />
          <Route path="/manager/login" element={
            <ManagerPublicRoute><ManagerLogin /></ManagerPublicRoute>
          } />

          {/* ── Manager Protected Routes ── */}
          <Route path="/manager/dashboard" element={
            <ManagerRoute><ManagerDashboard /></ManagerRoute>
          } />
          <Route path="/manager/companies" element={
            <ManagerRoute><ManagerCompanies /></ManagerRoute>
          } />
          <Route path="/manager/tickets" element={
            <ManagerRoute><ManagerTickets /></ManagerRoute>
          } />

          {/* LMS Public */}
          <Route path="/lms/register" element={
            <LMSRegister />
          } />
          <Route path="/lms/login"    element={
            <LMSLogin />
          } />

          {/* LMS Protected */}
          <Route path="/lms/dashboard" element=
            {<LMSProtectedRoute><LMSDashboard /></LMSProtectedRoute>
          } />
          <Route path="/lms/students"  element={
            <LMSProtectedRoute><LMSStudents /></LMSProtectedRoute>
          } />
          <Route path="/lms/classes"   element={
            <LMSProtectedRoute><LMSClasses /></LMSProtectedRoute>
          } />
          <Route path="/lms/timetable" element={
            <LMSProtectedRoute><LMSTimetable /></LMSProtectedRoute>
          } />
          <Route path="/lms/feed"      element={
            <LMSProtectedRoute><LMSFeed /></LMSProtectedRoute>
          } />
          <Route path="/lms/events"    element={
            <LMSProtectedRoute><LMSEvents /></LMSProtectedRoute>
          } />
          <Route path="/lms/reports"   element={
            <LMSProtectedRoute><LMSReports /></LMSProtectedRoute>
          } />
          <Route path="/lms/settings"  element={
            <LMSProtectedRoute><LMSSettings /></LMSProtectedRoute>
          } />
          <Route path="/lms/teachers"    element={
            <LMSProtectedRoute><LMSTeachers /></LMSProtectedRoute>} 
          />
          <Route path="/lms/assignments" element={
            <LMSProtectedRoute><LMSAssignments /></LMSProtectedRoute>} 
          />
          <Route path="/lms/attendance"  element={
            <LMSProtectedRoute><LMSAttendance /></LMSProtectedRoute>} 
          />
          <Route path="/lms/courses" element={
            <LMSProtectedRoute><LMSCourses /></LMSProtectedRoute>} 
          />
          <Route path="/lms/exams" element={
            <LMSProtectedRoute><LMSExams /></LMSProtectedRoute>}
          />
          <Route path="/lms/calendar" element={
            <LMSProtectedRoute><LMSCalendar /></LMSProtectedRoute>} 
          />
          <Route path="/lms/library" element={
            <LMSProtectedRoute><LMSLibrary /></LMSProtectedRoute>} 
          />
          <Route path="/lms/fees" element={
            <LMSProtectedRoute><LMSFees /></LMSProtectedRoute>} 
          />
          <Route path="/lms/notifications" element={
            <LMSProtectedRoute><LMSNotifications /></LMSProtectedRoute>} 
          />
          <Route path="/lms/access" element={
            <LMSProtectedRoute><LMSAccessControl /></LMSProtectedRoute>} 
          />

          <Route path="/lms/staff-login" element={
            <LMSStaffLogin />} 
          />


          {/* ── Catch all ── */}
          <Route path="*" element={
            <Navigate to={
              isAdminDomain ? '/admin/login'
              : isManagerDomain ? '/manager/login'
              : '/login'
            } replace />
          } />

        </Routes>
      </BrowserRouter>
      </LMSAuthProvider>
    </AuthProvider>
  );
}